import {CheckboxListOption, findLocale, formatFileSize, i18n, readFileAsBase64} from '@oebb-web-team/oebb-web-components';
import {Component, Element, Fragment, h, JSX, Listen, Method, Prop, State} from '@stencil/core';

import {
    ApartmentRelation,
    ApartmentStateDto,
    ApartmentType,
    ApiError,
    ApplicationRequestDto,
    Company,
    DocumentDto,
    DocumentType,
    FlatmateDto,
    FlatmateRelation,
    Gender,
    PersonalStatus
} from '../../http/wohnen-apps';

import {corpApartmentApplicationI18n} from '../../i18n/corp-apartment-application-i18n';
import {corpApartmentI18n} from '../../i18n/corp-apartment-i18n';

import {ApartmentApplicationStep, ApplicationService} from '../../services/application.service';
import {getDateString} from '../../utils/date';
import {getTotalFileSize} from '../../utils/files';
import {generateCheckboxListOptions, TypedFormData} from '../../utils/form-data';

// The types of apartments that are offered
const APARTMENT_TYPE_COOPERATIVE = 'Genossenschaftswohnung';
const APARTMENT_TYPE_EMPLOYEE = 'OebbMitarbeiterwohnung';

// Generate the options for select fields from the OpenAPI enum values
// We would need to list them manually only if we need to exclude values or sort them in a specific way
const GENDER_OPTIONS = Object.values(Gender);
const PERSONAL_STATUS_OPTIONS = Object.values(PersonalStatus);
const APARTMENT_TYPE_OPTIONS = Object.values(ApartmentType);
const APARTMENT_RELATION_OPTIONS = Object.values(ApartmentRelation);
const FLATMATE_RELATION_OPTIONS = Object.values(FlatmateRelation);
const COMPANY_OPTIONS = Object.values(Company);

// Customers must be at least 14 years old, flatmates can also be children
const MAX_BIRTHDATE_CUSTOMER = getDateString(14);
const MAX_BIRTHDATE_FLATMATE = getDateString(0);
const MAX_BIRTHDATE_ADULT = getDateString(18);
const MIN_BIRTHDATE = '1900-01-01';

// Only allow PDF documents
const DOCUMENT_FILE_TYPES = ['application/pdf'];

// Limit total file size
const DOCUMENT_MAX_SIZE = 20 * 1024 * 1024;

interface DocumentField {
    type: DocumentType;
    required?: boolean;
    adultOnly?: boolean;
    info?: string;
}

// Determine the required document types based on the apartment type
const DOCUMENT_FIELDS: {[key: string]: DocumentField[]} = {
    [APARTMENT_TYPE_COOPERATIVE]: [
        {type: DocumentType.MONTHLY_INCOME, required: true},
        {type: DocumentType.YEARLY_INCOME, required: true},
        {type: DocumentType.RESIDENCE_REGISTRATION, required: true},
        {type: DocumentType.EMPLOYER_CONFIRMATION, required: true},
        {type: DocumentType.PASSPORT, required: true},
        {type: DocumentType.NATIONALITY, required: true},
        {type: DocumentType.APPRENTICE_CONFIRMATION},
    ],
    [APARTMENT_TYPE_EMPLOYEE]: [
        {type: DocumentType.PERSONNEL_DATA, required: true, info: i18n.get('apartment.application.documents.info.PersonellData')},
        {type: DocumentType.INCOME_GROUP, required: true, info: i18n.get('apartment.application.documents.info.IncomeGroup')},
        {type: DocumentType.APPRENTICE_CONFIRMATION},
    ],
};

const DOCUMENT_FIELDS_FLATMATE: {[key: string]: DocumentField[]} = {
    [APARTMENT_TYPE_COOPERATIVE]: [
        {type: DocumentType.MONTHLY_INCOME, required: true, adultOnly: true},
        {type: DocumentType.YEARLY_INCOME, required: true, adultOnly: true},
        {type: DocumentType.RESIDENCE_REGISTRATION, required: true},
        {type: DocumentType.PASSPORT, required: true},
        {type: DocumentType.NATIONALITY, required: true},
    ],
};

interface FlatmateWithFormData {
    lastName?: string;
    firstName?: string;
    birthdate?: string;
    relationOptions: CheckboxListOption[];
    documentFiles: Partial<Record<DocumentType, File[]>>;
}

@Component({
    tag: 'corp-apartment-application',
    styleUrl: 'corp-apartment-application.scss',
})
export class CorpApartmentApplication {

    @Element()
    private el: HTMLElement;

    @Prop()
    appsUrl: string;

    @Prop()
    apartmentId: string;

    @Prop()
    wohnungsId: string;

    @Prop()
    gdprLink: string;

    @Prop()
    contactLink: string;

    @Prop()
    hideState: string;

    @Prop()
    hideApprenticeField: string;

    @State()
    private apartmentState: ApartmentStateDto;

    @State()
    private apartmentStateLoading: boolean;

    @State()
    private apartmentStateError: boolean;

    @State()
    private backendDisabled: boolean;

    @State()
    private documentsError: boolean;

    @State()
    private sendError: boolean;

    @State()
    private loading: boolean;

    @State()
    private valid: boolean = true;

    @State()
    private gdprAccepted = false;

    @State()
    private employeeValid = true;

    @State()
    private employeeAllowed = true;

    @State()
    private alreadyApplied = false;

    @State()
    private step = ApartmentApplicationStep.EMPLOYEE;

    @State()
    private flatmates: FlatmateWithFormData[];

    @State()
    private documentSize = 0;

    @State()
    private infoOpen: { [index: number]: boolean } = {};

    @State()
    private data: Partial<ApplicationRequestDto> = {};

    private documents: Partial<Record<DocumentType, File[]>> = {};

    private storeData: boolean;

    private genderOptions: CheckboxListOption[];
    private personalStatusOptions: CheckboxListOption[];
    private companyOptions: CheckboxListOption[];
    private currentApartmentTypeOptions: CheckboxListOption[];
    private currentApartmentRelationOptions: CheckboxListOption[];

    private sizeFormat: Intl.NumberFormat;
    private roomsFormat: Intl.NumberFormat;
    private currencyFormat: Intl.NumberFormat;

    private service: ApplicationService;

    componentWillLoad() {
        this.service = new ApplicationService(this.appsUrl);

        const lang = findLocale(this.el);
        i18n.setLang(lang);
        i18n.add(corpApartmentI18n);
        i18n.add(corpApartmentApplicationI18n);

        this.sizeFormat = new Intl.NumberFormat(lang, {maximumFractionDigits: 2});
        this.roomsFormat = new Intl.NumberFormat(lang, {maximumFractionDigits: 0});
        this.currencyFormat = new Intl.NumberFormat(lang, {style: 'currency', currency: 'EUR'});
    }

    @Method()
    async initData() {
        if (this.apartmentStateLoading) {
            // Do not load again if we are currently loading or already have data
            return;
        }

        try {
            // Check if the apartment is still online
            this.apartmentStateLoading = true;
            this.apartmentState = await this.service.getApartmentState(this.apartmentId);
            this.apartmentStateError = false;
            this.backendDisabled = false;
        } catch (e) {
            this.apartmentState = null;
            if (e instanceof ApiError && e.status === 503) {
                this.backendDisabled = true;
            } else {
                this.apartmentStateError = true;
                this.service.logClientError(this.data?.employeeId, 'apartment-state', e);
            }
        } finally {
            this.apartmentStateLoading = false;
        }

        if (this.apartmentState?.online && !this.data?.employeeId) {
            // Apartment is online, load the existing application
            const applicationData = this.service.loadApplicationData();
            this.step = ApartmentApplicationStep.EMPLOYEE;
            this.data = applicationData || {};
            this.updateComputedData();

            // If data is already stored, the user already agreed to storing it
            this.storeData = !!applicationData;

            // Focus the first form element after it is rendered
            setTimeout(() => this.focusFirstFormElement(), 50);
        }
    }

    private updateComputedData() {
        this.genderOptions =
            generateCheckboxListOptions(GENDER_OPTIONS, 'apartment.application.gender.', this.data.customer?.gender);
        this.personalStatusOptions =
            generateCheckboxListOptions(PERSONAL_STATUS_OPTIONS, 'apartment.application.personalStatus.', this.data.customer?.personalStatus);
        this.companyOptions =
            generateCheckboxListOptions(COMPANY_OPTIONS, '', this.data.customer?.company);
        this.currentApartmentTypeOptions =
            generateCheckboxListOptions(APARTMENT_TYPE_OPTIONS, 'apartment.application.apartmentType.', this.data.application?.currentApartmentType);
        this.currentApartmentRelationOptions =
            generateCheckboxListOptions(APARTMENT_RELATION_OPTIONS, 'apartment.application.apartmentRelation.', this.data.application?.currentApartmentRelation);

        this.flatmates = this.data.flatmates?.map((flatmate, index) => ({
            ...flatmate,
            relationOptions: generateCheckboxListOptions(FLATMATE_RELATION_OPTIONS, 'apartment.application.flatmate.relation.', flatmate.relation),
            documentFiles: this.flatmates?.[index]?.documentFiles || {},
        })) || [];
    }

    private readonly toggleInfo = (index: number) => {
        this.infoOpen = { ...this.infoOpen, [index]: !this.infoOpen[index] };
    }

    private readonly onCloseClick = (e: UIEvent) => {
        e.preventDefault();
        this.el.closest('dialog')?.close();
    };

    private readonly onPreviousStepClick = (e: UIEvent) => {
        e.preventDefault();
        if (this.step > 0) {
            this.setStep(this.step - 1);
        }
    };

    private readonly jumpToStep = (e: UIEvent) => {
        e.preventDefault();
        this.setStep(parseInt((e.target as HTMLElement).closest('button').dataset.step));
    };

    @Listen('change', {passive: true})
    onFormChanged() {
        this.updateData();
    }

    @Listen('invalid', {capture: true, passive: false})
    onFormInvalid(e: UIEvent) {
        e.preventDefault();
        this.valid = false;
        this.scrollToInvalidElement();
    }

    private handleRadioInputChange = (event: Event) => {
        const target = event.target as HTMLInputElement;
        this.data.customer = {
            ...this.data.customer,
            oebbRelation: target.value
        };
    };

    private readonly onFormSubmit = (e: UIEvent) => {
        e.preventDefault();
        this.nextStep().then();
    };

    private readonly onFlatmateAddClick = (e: UIEvent) => {
        e.preventDefault();
        this.flatmates = [
            ...this.flatmates, {
                relationOptions: generateCheckboxListOptions(FLATMATE_RELATION_OPTIONS, 'apartment.application.flatmate.relation.'),
                documentFiles: {},
            }
        ];

        // Focus the newly added flatmate after it is rendered
        setTimeout(() => this.el.querySelector<HTMLElement>('form fieldset:last-of-type label')?.focus(), 50);
    };

    private readonly onFlatmateRemoveClick = (e: UIEvent) => {
        e.preventDefault();
        const index = +(e.target as HTMLElement).dataset.index;
        this.flatmates = [...this.flatmates.slice(0, index), ...this.flatmates.slice(index + 1)];
    };

    private async nextStep() {
        await this.updateData();

        this.valid = await this.validateStep();
        if (this.valid) {
            if (this.step < ApartmentApplicationStep.CONFIRMATION) {
                this.setStep(this.step + 1);
            } else if (this.step === ApartmentApplicationStep.CONFIRMATION) {
                await this.sendApplication();
            }
        } else {
            this.scrollToInvalidElement();
        }
    }

    private scrollToInvalidElement() {
        const firstInvalidInput = document.querySelector(
            'input:invalid, select:invalid, textarea:invalid, .multiselect-invalid'
        ) as HTMLElement;

        if (firstInvalidInput) {
            const labelInvalidInput = firstInvalidInput.closest('label');
            if (labelInvalidInput) {
                labelInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else {
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    private updateData() {
        const formEl = this.el.querySelector('form');
        if (!formEl) {
            return;
        }

        const formData = new TypedFormData(formEl);
        switch (this.step) {
            case ApartmentApplicationStep.EMPLOYEE:
                this.data.employeeId = formData.getTrimmed('employeeId');
                this.data.accessCardNumber = formData.getTrimmed('accessCardNumber');
                this.storeData = !!formData.get('storeData');
                this.gdprAccepted = !!formData.get('gdpr');
                break;
            case ApartmentApplicationStep.CUSTOMER:
                this.data.customer = {
                    lastName: formData.getTrimmed('lastName'),
                    firstName: formData.getTrimmed('firstName'),
                    gender: formData.getTyped<Gender>('gender'),
                    birthdate: formData.getTrimmed('birthdate'),
                    nationality: formData.getTrimmed('nationality'),
                    personalStatus: formData.getTyped<PersonalStatus>('personalStatus'),
                    oebbRelation: formData.getTrimmed('oebbRelation'),
                    phone: formData.getTrimmed('phone'),
                    email: formData.getTrimmed('email'),
                    company: formData.getTyped<Company>('company'),
                    address: {
                        street: formData.getTrimmed('addressStreet'),
                        number: formData.getTrimmed('addressNumber'),
                        staircase: formData.getTrimmed('addressStaircase'),
                        floor: formData.getTrimmed('addressFloor'),
                        apartment: formData.getTrimmed('addressApartment'),
                        postalCode: formData.getTrimmed('addressPostalCode'),
                        city: formData.getTrimmed('addressCity'),
                        state: formData.getTrimmed('addressState'),
                        country: formData.getTrimmed('addressCountry'),
                    },
                };
                break;
            case ApartmentApplicationStep.APPLICATION:
                this.data.application = {
                    currentApartmentType: formData.getTyped<ApartmentType>('currentApartmentType'),
                    currentApartmentRelation: formData.getTyped<ApartmentRelation>('currentApartmentRelation'),
                    currentLandlord: formData.getTrimmed('currentLandlord'),
                    currentRooms: formData.getInt('currentRooms'),
                    currentSize: formData.getFloat('currentSize'),
                    currentRent: formData.getFloat('currentRent'),
                    familyNetIncome: formData.getFloat('familyNetIncome'),
                    reason: formData.getTrimmed('reason'),
                };
                break;
            case ApartmentApplicationStep.FLATMATES:
                this.data.flatmates = this.flatmates.map((_, index) => ({
                    lastName: formData.getTrimmed(`flatmate${index}LastName`),
                    firstName: formData.getTrimmed(`flatmate${index}FirstName`),
                    birthdate: formData.getTrimmed(`flatmate${index}Birthdate`),
                    relation: formData.getTyped<FlatmateRelation>(`flatmate${index}Relation`),
                    documents: null,
                }));
                this.updateComputedData();
                break;
            case ApartmentApplicationStep.DOCUMENTS:
                this.documentSize = 0;

                this.documents = {};
                DOCUMENT_FIELDS[this.apartmentState?.apartmentType]?.forEach(field => {
                    this.documents[field.type] = formData.getFiles(field.type);
                    this.documentSize += getTotalFileSize(this.documents[field.type]);
                });

                this.flatmates.forEach((flatmate, index) => {
                    flatmate.documentFiles = {};
                    this.getFlatmateDocumentFields(flatmate)?.forEach(field => {
                        flatmate.documentFiles[field.type] = formData.getFiles(`flatmate${index}${field.type}`);
                        this.documentSize += getTotalFileSize(flatmate.documentFiles[field.type]);
                    });
                });

                break;
        }

        this.data = {...this.data};

        if (this.storeData) {
            this.service.saveApplicationData(this.data);
        } else {
            this.service.deleteApplicationData();
        }
    }

    private async validateStep(): Promise<boolean> {
        switch (this.step) {
            case ApartmentApplicationStep.EMPLOYEE:
                if (!this.data.employeeId || !this.data.accessCardNumber) {
                    return false;
                }

                try {
                    this.loading = true;
                    const response = await this.service.startApplication(this.wohnungsId, this.data.employeeId, this.data.accessCardNumber);

                    this.employeeValid = response.employeeExists;
                    this.employeeAllowed = response.applicationAllowed;
                    this.alreadyApplied = response.applicationExists;
                    if (!this.employeeValid || !this.employeeAllowed || this.alreadyApplied) {
                        return false;
                    }

                    this.data.employeeId = response.employeeId;
                    return true;
                } catch (e) {
                    if (e instanceof ApiError && e.status === 503) {
                        this.backendDisabled = true;
                    } else {
                        console.log('Error verifying employee data', e);
                        this.employeeValid = false;
                        this.service.logClientError(this.data.employeeId, 'verify-employee', e);
                        return false;
                    }
                } finally {
                    this.loading = false;
                }
            case ApartmentApplicationStep.CUSTOMER:
                return !!this.data.customer?.gender && !!this.data.customer?.personalStatus && !!this.data.customer?.company;
            case ApartmentApplicationStep.APPLICATION:
                return !!this.data.application?.currentApartmentType && !!this.data.application?.currentApartmentRelation;
            case ApartmentApplicationStep.FLATMATES:
                return !this.data.flatmates?.find(item => !item.relation);
            case ApartmentApplicationStep.DOCUMENTS:
                return this.documentSize <= DOCUMENT_MAX_SIZE
                    && this.validateRequiredDocuments(DOCUMENT_FIELDS[this.apartmentState?.apartmentType], this.documents)
                    && !this.flatmates.find(flatmate =>
                        !this.validateRequiredDocuments(this.getFlatmateDocumentFields(flatmate), flatmate.documentFiles)
                    );
        }

        return true;
    }

    private validateRequiredDocuments(fields: DocumentField[], documents: Partial<Record<DocumentType, File[]>>) {
        return !fields?.find(field => field.required && !documents[field.type]?.length);
    }

    private setStep(step: ApartmentApplicationStep) {
        if (this.step === step) {
            return;
        }

        this.step = step;

        // Reset validity flags
        this.valid = true;
        this.documentsError = false;
        this.sendError = false;

        // Reset all input fields, because Stencil reuses DOM elements if possible
        this.el.querySelector('form').reset();

        // Scroll the content to top
        this.el.querySelector<HTMLElement>('.apartment-application-content')?.scrollTo({top: 0});

        // Focus the first form element after it is rendered
        setTimeout(() => this.focusFirstFormElement(), 50);
    }

    private focusFirstFormElement() {
        let el = this.el.querySelector<HTMLElement>('form label, form oebbx-multiselect');
        if (el?.shadowRoot) {
            el = el.shadowRoot.querySelector<HTMLElement>('[tabindex], label, input');
        }

        el?.focus({preventScroll: true});
    }

    private async readDocuments(fields: DocumentField[], files: Partial<Record<DocumentType, File[]>>) {
        try {
            const documents: Partial<Record<DocumentType, DocumentDto[]>> = {};
            await Promise.all(fields?.map(async field =>
                documents[field.type] = await Promise.all(files[field.type]?.map(async file => ({
                    type: field.type,
                    filename: file.name,
                    data: await readFileAsBase64(file),
                })) || [])
            ) || []);
            return documents;
        } catch (e) {
            console.log('Error reading documents:', e);
            this.service.logClientError(this.data?.employeeId, 'read-documents', e);
            return null;
        }
    }

    private async sendApplication() {
        try {
            this.loading = true;
            this.documentsError = false;
            this.sendError = false;

            const documents = await this.readDocuments(DOCUMENT_FIELDS[this.apartmentState?.apartmentType], this.documents);
            if (!documents) {
                this.documentsError = true;
                return;
            }

            const flatmates = [];
            for (let i = 0; i < this.data.flatmates.length; i++) {
                const flatmate = this.data.flatmates[i];
                const flatmateDocuments = await this.readDocuments(this.getFlatmateDocumentFields(flatmate), this.flatmates[i]?.documentFiles);
                if (!flatmateDocuments) {
                    this.documentsError = true;
                    return;
                }

                flatmates.push({
                    ...flatmate,
                    documents: flatmateDocuments,
                });
            }

            const result = await this.service.sendApplication(this.apartmentId, {
                employeeId: this.data.employeeId,
                accessCardNumber: this.data.accessCardNumber,
                customer: this.data.customer,
                application: this.data.application,
                flatmates,
                documents,
            });

            if (result?.success) {
                this.setStep(ApartmentApplicationStep.DONE);
            } else {
                console.log('Sending application was not successful:', result);
                this.sendError = true;
                this.service.logClientError(this.data?.employeeId, 'send-application', {
                    name: 'ApplicationSuccessError',
                    message: JSON.stringify(result ?? null),
                });
            }
        } catch (e) {
            if (e instanceof ApiError && e.status === 503) {
                this.backendDisabled = true;
            } else {
                console.log('Error sending application:', e);
                this.sendError = true;
                this.service.logClientError(this.data?.employeeId, 'send-application', e);
            }
        } finally {
            this.loading = false;
        }
    }

    private getFlatmateDocumentFields(flatmate: Partial<FlatmateDto>): DocumentField[] {
        return flatmate.birthdate > MAX_BIRTHDATE_ADULT
            ? DOCUMENT_FIELDS_FLATMATE[this.apartmentState?.apartmentType]?.filter(field => !field.adultOnly)
            : DOCUMENT_FIELDS_FLATMATE[this.apartmentState?.apartmentType];
    }

    private renderPreviousButton(): JSX.Element {
        return (
            <button class="apartment-application-button apartment-application-button-secondary apartment-application-button-back"
                    type="button" onClick={this.onPreviousStepClick}>
                <oebbx-icon name="triangle" svgStyle={null} />
                {i18n.get('apartment.application.previousStep')}
            </button>
        );
    }

    private renderSubmitButton(title = 'apartment.application.nextStep', disabled = false): JSX.Element {
        return (
            <button class="apartment-application-button apartment-application-button-primary" type="submit" disabled={disabled}>
                {i18n.get(title)}
                <oebbx-icon name="triangle" svgStyle={null} />
            </button>
        );
    }

    private renderEmployeeError(): JSX.Element {
        if (this.valid) {
            return null;
        }

        if (!this.gdprAccepted) {
            return (
                <p class="apartment-application-error">
                    {i18n.get('apartment.application.gdprMissing')}
                </p>
            );
        }

        if (!this.employeeValid) {
            return (
                <p class="apartment-application-error">
                    {i18n.get('apartment.application.employee.invalid')}
                </p>
            );
        }

        if (this.alreadyApplied) {
            return (
                <p class="apartment-application-error">
                    {i18n.get('apartment.application.employee.alreadyApplied')}
                </p>
            );
        }

        if (!this.employeeAllowed) {
            return (
                <p class="apartment-application-error">
                    {i18n.get('apartment.application.employee.offLimits')}
                </p>
            );
        }

        return null;
    }

    private renderEmployeeData(): JSX.Element {
        return (
            <Fragment>
                <div class="apartment-application-content">
                    <div class="apartment-application-intro">
                        <p>{i18n.get('apartment.application.employee.description')}</p>
                        <p innerHTML={i18n.get('apartment.application.employee.retired', null, [this.contactLink])}></p>
                    </div>

                    <div class="apartment-application-grid">
                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.employee.employeeId')}</span>
                            <input type="text"
                                   name="employeeId"
                                   placeholder={i18n.get('apartment.application.employee.employeeIdPlaceholder')}
                                   required={true}
                                   pattern="(z|\d+-)?0*([1-9]\d+)"
                                   title={i18n.get('apartment.application.employee.employeeId.help')}
                                   value={this.data.employeeId ?? ''}
                            />
                        </label>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.employee.accessCardNumber')}</span>
                            <input type="text"
                                   name="accessCardNumber"
                                   placeholder={i18n.get('apartment.application.employee.accessCardNumberPlaceholder')}
                                   required={true}
                                   pattern="([A-Za-z]-)?0*([1-9]\d+)(-\d+)?"
                                   title={i18n.get('apartment.application.employee.accessCardNumber.help')}
                                   value={this.data.accessCardNumber ?? ''}
                            />
                        </label>
                    </div>

                    <p>
                        {i18n.get('apartment.application.employee.apply')}
                        <a href={i18n.get('apartment.application.employee.jobs-url')} target="_blank">
                            {i18n.get('apartment.application.employee.jobs-text')}
                        </a>
                    </p>

                    <div class="apartment-application-checkboxes apartment-application-gdpr">
                        <label class="apartment-application-label">
                            <input type="checkbox" name="storeData" checked={this.storeData} />
                            <span class="form-label-text">{i18n.get('apartment.application.storeData')}</span>
                        </label>

                        <label class="apartment-application-label">
                            <input type="checkbox" name="gdpr" required={true} checked={this.gdprAccepted} />
                            <div>
                                <span class="form-label-text">{i18n.get('apartment.application.gdprText')}</span>
                                <p>
                                    <a href={this.gdprLink} target="_blank">{i18n.get('apartment.application.gdprLink')}</a>
                                </p>
                            </div>
                        </label>
                    </div>
                </div>

                {this.renderEmployeeError()}

                <div class="apartment-application-buttons">
                    {this.loading ? (
                        <div class="apartment-application-loading"></div>
                    ) : null}
                    {this.renderSubmitButton('apartment.application.employee.submit', this.loading)}
                </div>
            </Fragment>
        );
    }

    private renderCustomerData(): JSX.Element {
        return (
            <Fragment>
                <div class="apartment-application-content">
                    <fieldset class="apartment-application-grid">
                        <legend>{i18n.get('apartment.application.customer.personal')}</legend>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.person.lastName')}</span>
                            <input type="text"
                                   name="lastName"
                                   placeholder={i18n.get('apartment.application.person.lastName')}
                                   required={true}
                                   pattern=".*\S.*"
                                   maxlength={255}
                                   value={this.data.customer?.lastName ?? ''}
                            />
                        </label>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.person.firstName')}</span>
                            <input type="text"
                                   name="firstName"
                                   placeholder={i18n.get('apartment.application.person.firstName')}
                                   required={true}
                                   pattern=".*\S.*"
                                   maxlength={255}
                                   value={this.data.customer?.firstName ?? ''}
                            />
                        </label>

                        <div class={{'multiselect-invalid': !this.data.customer?.gender}}>
                            <oebbx-multiselect class={{'invalid': !this.data.customer?.gender}}
                                            name="gender"
                                            placeholder={i18n.get('apartment.application.person.gender')}
                                            aria-label={i18n.get('apartment.application.person.gender')}
                                            floatingLabel={false}
                                            options={this.genderOptions}
                                            multiple={false}
                                            required={true}
                                            showValidation={!this.valid}
                                            showToggler={true}
                            ></oebbx-multiselect>
                        </div>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.person.birthdate')}</span>
                            <input type="date"
                                   name="birthdate"
                                   placeholder={i18n.get('apartment.application.person.birthdate')}
                                   required={true}
                                   min={MIN_BIRTHDATE}
                                   max={MAX_BIRTHDATE_CUSTOMER}
                                   value={this.data.customer?.birthdate ?? ''}
                            />
                        </label>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.person.nationality')}</span>
                            <input type="text"
                                   name="nationality"
                                   placeholder={i18n.get('apartment.application.person.nationality')}
                                   required={true}
                                   pattern=".*\S.*"
                                   maxlength={50}
                                   value={this.data.customer?.nationality ?? ''}
                            />
                        </label>

                        <div class={{'multiselect-invalid': !this.data.customer?.personalStatus}}>
                            <oebbx-multiselect name="personalStatus"
                                            placeholder={i18n.get('apartment.application.person.personalStatus')}
                                            aria-label={i18n.get('apartment.application.person.personalStatus')}
                                            floatingLabel={false}
                                            options={this.personalStatusOptions}
                                            multiple={false}
                                            required={true}
                                            showValidation={!this.valid}
                                            showToggler={true}
                            ></oebbx-multiselect>
                        </div>
                    </fieldset>

                    <fieldset class="apartment-application-grid">
                        <legend>{i18n.get('apartment.application.customer.address')}</legend>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.customer.address.street')}</span>
                            <input type="text"
                                   name="addressStreet"
                                   placeholder={i18n.get('apartment.application.customer.address.street')}
                                   required={true}
                                   pattern=".*\S.*"
                                   maxlength={255}
                                   value={this.data.customer?.address?.street ?? ''}
                            />
                        </label>

                        <div class="apartment-application-address-number">
                            <label class="apartment-application-label apartment-application-required">
                                <span class="form-label-text">{i18n.get('apartment.application.customer.address.number')}</span>
                                <input type="text"
                                       name="addressNumber"
                                       placeholder={i18n.get('apartment.application.customer.address.number')}
                                       required={true}
                                       pattern="\d+([a-zA-Z]|-\d+)?"
                                       maxlength={30}
                                       value={this.data.customer?.address?.number ?? ''}
                                />
                            </label>

                            <label class="apartment-application-label">
                                <span class="form-label-text">{i18n.get('apartment.application.customer.address.staircase')}</span>
                                <input type="text"
                                       name="addressStaircase"
                                       placeholder={i18n.get('apartment.application.customer.address.staircase')}
                                       maxlength={30}
                                       value={this.data.customer?.address?.staircase ?? ''}
                                />
                            </label>

                            <label class="apartment-application-label">
                                <span class="form-label-text">{i18n.get('apartment.application.customer.address.floor')}</span>
                                <input type="text"
                                       name="addressFloor"
                                       placeholder={i18n.get('apartment.application.customer.address.floor')}
                                       maxlength={30}
                                       value={this.data.customer?.address?.floor ?? ''}
                                />
                            </label>

                            <label class="apartment-application-label">
                                <span class="form-label-text">{i18n.get('apartment.application.customer.address.apartment')}</span>
                                <input type="text"
                                       name="addressApartment"
                                       placeholder={i18n.get('apartment.application.customer.address.apartment')}
                                       maxlength={30}
                                       value={this.data.customer?.address?.apartment ?? ''}
                                />
                            </label>
                        </div>

                        <div class="apartment-application-address-city">
                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.customer.address.postalCode')}</span>
                            <input type="text"
                                   name="addressPostalCode"
                                   placeholder={i18n.get('apartment.application.customer.address.postalCode')}
                                   required={true}
                                   pattern="\d{4,6}"
                                   value={this.data.customer?.address?.postalCode ?? ''}
                            />
                        </label>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.customer.address.city')}</span>
                            <input type="text"
                                   name="addressCity"
                                   placeholder={i18n.get('apartment.application.customer.address.city')}
                                   required={true}
                                   pattern=".*\S.*"
                                   maxlength={255}
                                   value={this.data.customer?.address?.city ?? ''}
                            />
                        </label>
                        </div>

                        {! (this.hideState === "true") && (
                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.customer.address.state')}</span>
                            <input type="text"
                                   name="addressState"
                                   placeholder={i18n.get('apartment.application.customer.address.state')}
                                   required={true}
                                   pattern=".*\S.*"
                                   maxlength={255}
                                   value={this.data.customer?.address?.state ?? ''}
                            />
                        </label>
                        )}

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.customer.address.country')}</span>
                            <input type="text"
                                   name="addressCountry"
                                   placeholder={i18n.get('apartment.application.customer.address.country')}
                                   required={true}
                                   pattern=".*\S.*"
                                   maxlength={255}
                                   value={this.data.customer?.address?.country ?? ''}
                            />
                        </label>
                    </fieldset>

                    <fieldset class="apartment-application-grid">
                        <legend>{i18n.get('apartment.application.customer.contact')}</legend>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.customer.phone')}</span>
                            <input type="tel"
                                   name="phone"
                                   placeholder={i18n.get('apartment.application.customer.phone')}
                                   required={true}
                                   pattern="(\+[1-9]|0)[\d\s\(\)\/\.\-]{8,}"
                                   maxlength={20}
                                   value={this.data.customer?.phone ?? ''}
                            />
                        </label>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.customer.email')}</span>
                            <input type="email"
                                   name="email"
                                   placeholder={i18n.get('apartment.application.customer.email')}
                                   required={true}
                                   value={this.data.customer?.email ?? ''}
                            />
                        </label>
                    </fieldset>

                    <fieldset class="apartment-application-grid">
                        <legend>{i18n.get('apartment.application.customer.employee')}</legend>

                        <div class={{'multiselect-invalid': !this.data.customer?.company}}>
                            <oebbx-multiselect name="company"
                                            placeholder={i18n.get('apartment.application.customer.company')}
                                            aria-label={i18n.get('apartment.application.customer.company')}
                                            floatingLabel={false}
                                            options={this.companyOptions}
                                            multiple={false}
                                            required={true}
                                            showValidation={!this.valid}
                                            showToggler={true}
                            ></oebbx-multiselect>
                        </div>

                        <label class="apartment-application-label">
                            <span class="form-label-text">{i18n.get('apartment.application.employee.employeeId')}</span>
                            <input type="text"
                                   readonly={true}
                                   value={this.data.employeeId ?? ''}
                            />
                        </label>

                        {! (this.hideApprenticeField === "true") && (
                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.employee.apprentice.label')}</span>
                            <fieldset class="radio-group" aria-required="true">
                                <label class="custom-radio">
                                    <input
                                        type="radio"
                                        id="oebb-ja"
                                        name="oebbRelation"
                                        value={i18n.get('apartment.application.employee.apprentice.yes')}
                                        required
                                        checked={this.data.customer?.oebbRelation === "Lehrling"}
                                        onChange={this.handleRadioInputChange} />
                                    <span>Ja</span>
                                </label>

                                <label class="custom-radio">
                                    <input
                                        type="radio"
                                        id="oebb-nein"
                                        name="oebbRelation"
                                        value={i18n.get('apartment.application.employee.apprentice.no')}
                                        required
                                        checked={this.data.customer?.oebbRelation === "kein Lehrling"}
                                        onChange={this.handleRadioInputChange} />
                                    <span>Nein</span>
                                </label>
                            </fieldset>
                        </label>
                        )}

                    </fieldset>
                </div>

                {!this.valid ? (
                    <p class="apartment-application-error">{i18n.get('apartment.application.invalid')}</p>
                ) : null}

                <div class="apartment-application-buttons">
                    {this.renderPreviousButton()}
                    {this.renderSubmitButton()}
                </div>
            </Fragment>
        );
    }

    private renderApplicationData(): JSX.Element {
        return (
            <Fragment>
                <div class="apartment-application-content">
                    <fieldset class="apartment-application-grid">
                        <legend>{i18n.get('apartment.application.current')}</legend>

                        <div class={{'multiselect-invalid': !this.data.application?.currentApartmentType}}>
                            <oebbx-multiselect name="currentApartmentType"
                                            placeholder={i18n.get('apartment.application.current.apartmentType')}
                                            aria-label={i18n.get('apartment.application.current.apartmentType')}
                                            floatingLabel={false}
                                            options={this.currentApartmentTypeOptions}
                                            multiple={false}
                                            required={true}
                                            showValidation={!this.valid}
                                            showToggler={true}
                            ></oebbx-multiselect>
                        </div>

                        <div class={{'multiselect-invalid': !this.data.application?.currentApartmentRelation}}>
                            <oebbx-multiselect name="currentApartmentRelation"
                                            placeholder={i18n.get('apartment.application.current.apartmentRelation')}
                                            aria-label={i18n.get('apartment.application.current.apartmentRelation')}
                                            floatingLabel={false}
                                            options={this.currentApartmentRelationOptions}
                                            multiple={false}
                                            required={true}
                                            showValidation={!this.valid}
                                            showToggler={true}
                            ></oebbx-multiselect>
                        </div>

                        <label class="apartment-application-label">
                            <span class="form-label-text">{i18n.get('apartment.application.current.landlord')}</span>
                            <input type="text"
                                   name="currentLandlord"
                                   placeholder={i18n.get('apartment.application.current.landlord')}
                                   maxlength={255}
                                   value={this.data.application?.currentLandlord ?? ''}
                            />
                        </label>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.current.rooms')}</span>
                            <input type="number"
                                   name="currentRooms"
                                   placeholder={i18n.get('apartment.application.current.rooms')}
                                   required={true}
                                   min={1}
                                   max={30}
                                   step={1}
                                   value={this.data.application?.currentRooms ?? ''}
                            />
                        </label>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.current.size')}</span>
                            <input type="number"
                                   name="currentSize"
                                   placeholder={i18n.get('apartment.application.current.size')}
                                   required={true}
                                   min={1}
                                   max={10_000}
                                   step={0.01}
                                   value={this.data.application?.currentSize ?? ''}
                            />
                        </label>

                        <label class="apartment-application-label apartment-application-required">
                            <span class="form-label-text">{i18n.get('apartment.application.current.rent')}</span>
                            <input type="number"
                                   name="currentRent"
                                   placeholder={i18n.get('apartment.application.current.rent')}
                                   required={true}
                                   min={1}
                                   max={10_000}
                                   step={0.01}
                                   value={this.data.application?.currentRent ?? ''}
                            />
                        </label>
                    </fieldset>

                    <fieldset class="apartment-application-grid">
                        <legend>{i18n.get('apartment.application.additional')}</legend>

                        {this.apartmentState?.apartmentType === APARTMENT_TYPE_COOPERATIVE ? (
                            <label class="apartment-application-label apartment-application-required">
                                <span class="form-label-text">{i18n.get('apartment.application.familyNetIncome')}</span>
                                <input type="number"
                                       name="familyNetIncome"
                                       placeholder={i18n.get('apartment.application.familyNetIncome')}
                                       required={true}
                                       min={1}
                                       max={1_000_000}
                                       step={0.01}
                                       value={this.data.application?.familyNetIncome ?? ''}
                                />
                            </label>
                        ) : null}

                        <label class="apartment-application-label apartment-application-required apartment-application-form-span">
                            <span class="form-label-text">{i18n.get('apartment.application.reason')}</span>
                            <input type="text"
                                   name="reason"
                                   placeholder={i18n.get('apartment.application.reason')}
                                   required={true}
                                   pattern=".*\S.*"
                                   maxlength={255}
                                   value={this.data.application?.reason ?? ''}
                            />
                        </label>
                    </fieldset>
                </div>

                {!this.valid ? (
                    <p class="apartment-application-error">{i18n.get('apartment.application.invalid')}</p>
                ) : null}

                <div class="apartment-application-buttons">
                    {this.renderPreviousButton()}
                    {this.renderSubmitButton()}
                </div>
            </Fragment>
        );
    }

    private renderFlatmates(): JSX.Element {
        return (
            <Fragment>
                <div class="apartment-application-content">
                    {this.flatmates?.map((flatmate, index) => (
                        <fieldset class="apartment-application-grid">
                            <legend>{i18n.get('apartment.application.flatmate', null, [(index + 1).toString()])}</legend>

                            <label class="apartment-application-label apartment-application-required">
                                <span class="form-label-text">{i18n.get('apartment.application.person.lastName')}</span>
                                <input type="text"
                                       name={`flatmate${index}LastName`}
                                       placeholder={i18n.get('apartment.application.person.lastName')}
                                       required={true}
                                       pattern=".*\S.*"
                                       maxlength={255}
                                       value={flatmate.lastName ?? ''}
                                />
                            </label>

                            <label class="apartment-application-label apartment-application-required">
                                <span class="form-label-text">{i18n.get('apartment.application.person.firstName')}</span>
                                <input type="text"
                                       name={`flatmate${index}FirstName`}
                                       placeholder={i18n.get('apartment.application.person.firstName')}
                                       required={true}
                                       pattern=".*\S.*"
                                       maxlength={255}
                                       value={flatmate.firstName ?? ''}
                                />
                            </label>

                            <label class="apartment-application-label apartment-application-required">
                                <span class="form-label-text">{i18n.get('apartment.application.person.birthdate')}</span>
                                <input type="date"
                                       name={`flatmate${index}Birthdate`}
                                       placeholder={i18n.get('apartment.application.person.birthdate')}
                                       required={true}
                                       min={MIN_BIRTHDATE}
                                       max={MAX_BIRTHDATE_FLATMATE}
                                       value={flatmate.birthdate ?? ''}
                                />
                            </label>

                            <div class={{'multiselect-invalid': !this.data?.flatmates?.[index]?.relation}}>
                                <oebbx-multiselect name={`flatmate${index}Relation`}
                                                placeholder={i18n.get('apartment.application.flatmate.relation')}
                                                aria-label={i18n.get('apartment.application.flatmate.relation')}
                                                floatingLabel={false}
                                                options={flatmate.relationOptions}
                                                multiple={false}
                                                required={true}
                                                showValidation={!this.valid}
                                                showToggler={true}
                                ></oebbx-multiselect>
                            </div>

                            <button class="apartment-application-button apartment-application-button-secondary apartment-application-flatmate-remove"
                                    type="button" data-index={index} onClick={this.onFlatmateRemoveClick}>
                                <oebbx-icon name="close" svgStyle={null} />
                                {i18n.get('apartment.application.flatmate.remove', null, [(index + 1).toString()])}
                            </button>
                        </fieldset>
                    ))}

                    <button class="apartment-application-button apartment-application-button-primary" type="button" onClick={this.onFlatmateAddClick}>
                        {i18n.get('apartment.application.flatmate.add')}
                    </button>
                </div>

                {!this.valid ? (
                    <p class="apartment-application-error">{i18n.get('apartment.application.invalid')}</p>
                ) : null}

                <div class="apartment-application-buttons">
                    {this.renderPreviousButton()}
                    {this.renderSubmitButton(this.flatmates.length ? 'apartment.application.nextStep' : 'apartment.application.flatmate.skip')}
                </div>
            </Fragment>
        );
    }

    private renderFlatmateDocuments(flatmate: FlatmateWithFormData, index: number): JSX.Element {
        const fields = this.getFlatmateDocumentFields(flatmate);
        return fields?.length ? (
            <fieldset class="apartment-application-grid">
                <legend>
                    {i18n.get('apartment.application.flatmate', null, [(index + 1).toString()])} ({flatmate.firstName} {flatmate.lastName})
                </legend>

                {fields.map(field => (
                    <label class={{'apartment-application-label': true, 'apartment-application-required': field.required}}>
                        <span class="form-label-text">{i18n.get(`apartment.application.documents.${field.type}`)}</span>
                        <oebbx-filepicker name={`flatmate${index}${field.type}`} types={DOCUMENT_FILE_TYPES}
                                          files={flatmate.documentFiles[field.type] || []}></oebbx-filepicker>
                    </label>
                ))}
            </fieldset>
        ) : null;
    }

    private renderDocuments(): JSX.Element {
        return (
            <Fragment>
                <div class="apartment-application-content">
                    <fieldset class="apartment-application-grid">
                        <legend>
                            {i18n.get('apartment.application.documents.mainRenter')} ({this.data.customer?.firstName} {this.data.customer?.lastName})
                        </legend>

                        {DOCUMENT_FIELDS[this.apartmentState?.apartmentType]?.map((field, index) => (
                            <label class={{'apartment-application-label': true, 'apartment-application-required': field.required}}>
                                <span class="form-label-text form-label-text-info">{i18n.get(`apartment.application.documents.${field.type}`)}</span>
                                <span>{field.info ?
                                <oebbx-icon name="info" svgStyle={null} title="Weitere Infos" onClick={() => this.toggleInfo(index)} />
                                : ''}
                                </span>
                                <oebbx-filepicker name={field.type} types={DOCUMENT_FILE_TYPES} files={this.documents[field.type] || []}></oebbx-filepicker>
                                {field.info && this.infoOpen[index] ?
                                    <div class="file-upload-info" innerHTML={field.info}>
                                    </div>
                                    : ''
                                }
                            </label>
                        ))}
                    </fieldset>

                    {this.flatmates?.map((flatmate, index) => this.renderFlatmateDocuments(flatmate, index))}
                </div>

                {this.documentSize > DOCUMENT_MAX_SIZE ? (
                    <p class="apartment-application-error">
                        {i18n.get('apartment.application.documents.maxSize', null, [formatFileSize(DOCUMENT_MAX_SIZE), formatFileSize(this.documentSize)])}
                    </p>
                ) : null}

                {!this.valid ? (
                    <p class="apartment-application-error">{i18n.get('apartment.application.invalid')}</p>
                ) : null}

                <div class="apartment-application-buttons">
                    {this.renderPreviousButton()}
                    {this.renderSubmitButton()}
                </div>
            </Fragment>
        );
    }

    private renderConfirmation(): JSX.Element {
        return (
            <Fragment>
                <div class="apartment-application-content apartment-application-confirmation">
                    <h3>{i18n.get('apartment.application.steps.1')}</h3>
                    <div class="apartments-row">
                        <div>
                            <table>
                                <tbody>
                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.person.lastName')}</th>
                                        <td>{this.data.customer?.lastName}</td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.person.firstName')}</th>
                                        <td>{this.data.customer?.firstName}</td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.person.gender')}</th>
                                        <td>
                                            {this.data.customer?.gender
                                                ? i18n.get(`apartment.application.gender.${this.data.customer.gender}`)
                                                : null
                                            }
                                        </td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.person.birthdate')}</th>
                                        <td>
                                            {this.data.customer?.birthdate
                                                ? i18n.get('apartment.application.person.birthdate.format', null, this.data.customer.birthdate.split('-'))
                                                : null
                                            }
                                        </td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.person.nationality')}</th>
                                        <td>{this.data.customer?.nationality}</td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.person.personalStatus')}</th>
                                        <td>
                                            {this.data.customer?.personalStatus
                                                ? i18n.get(`apartment.application.personalStatus.${this.data.customer.personalStatus}`)
                                                : null
                                            }
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div>
                            <table>
                                <tbody>
                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.customer.address')}</th>
                                        <td>
                                            {this.data.customer?.address?.street} {this.data.customer?.address?.number}
                                            {this.data.customer?.address?.staircase ? `/${this.data.customer.address.staircase}` : null}
                                            {this.data.customer?.address?.floor ? `/${this.data.customer.address.floor}` : null}
                                            {this.data.customer?.address?.apartment ? `/${this.data.customer.address.apartment}` : null}
                                            <br />
                                            {this.data.customer?.address?.postalCode} {this.data.customer?.address?.city}
                                            {this.data.customer?.address?.state ? `, ${this.data.customer.address.state}` : ''}
                                            , {this.data.customer?.address?.country}
                                        </td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.customer.phone')}</th>
                                        <td>{this.data.customer?.phone}</td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.customer.email')}</th>
                                        <td>{this.data.customer?.email}</td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.customer.company')}</th>
                                        <td>{this.data.customer?.company}</td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.employee.employeeId')}</th>
                                        <td>{this.data.employeeId}</td>
                                    </tr>

                                    {! (this.hideApprenticeField === "true") && (
                                        <tr>
                                            <th scope="row">{i18n.get('apartment.application.employee.apprentice')}</th>
                                            <td>{this.data.customer?.oebbRelation}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="apartments-row">
                        <div>
                            <h3>{i18n.get('apartment.application.steps.2')}</h3>
                            <table>
                                <tbody>
                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.current.apartmentType')}</th>
                                        <td>
                                            {this.data.application?.currentApartmentType
                                                ? i18n.get(`apartment.application.apartmentType.${this.data.application.currentApartmentType}`)
                                                : null
                                            }
                                        </td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.current.apartmentRelation')}</th>
                                        <td>
                                            {this.data.application?.currentApartmentRelation
                                                ? i18n.get(`apartment.application.apartmentRelation.${this.data.application.currentApartmentRelation}`)
                                                : null
                                            }
                                        </td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.current.landlord')}</th>
                                        <td>{this.data.application?.currentLandlord}</td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.current.rooms')}</th>
                                        <td>
                                            {this.data.application?.currentRooms
                                                ? i18n.get('apartment.format.rooms', null, this.roomsFormat.format(this.data.application.currentRooms))
                                                : null
                                            }
                                        </td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.current.size')}</th>
                                        <td>
                                            {this.data.application?.currentSize
                                                ? `${this.sizeFormat.format(this.data.application.currentSize)} m²`
                                                : null
                                            }
                                        </td>
                                    </tr>

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.current.rent')}</th>
                                        <td>
                                            {this.data.application?.currentRent
                                                ? this.currencyFormat.format(this.data.application.currentRent)
                                                : null
                                            }
                                        </td>
                                    </tr>

                                    {this.apartmentState?.apartmentType === APARTMENT_TYPE_COOPERATIVE ? (
                                        <tr>
                                            <th scope="row">{i18n.get('apartment.application.familyNetIncome')}</th>
                                            <td>
                                                {this.data.application?.familyNetIncome
                                                    ? this.currencyFormat.format(this.data.application.familyNetIncome)
                                                    : null
                                                }
                                            </td>
                                        </tr>
                                    ) : null}

                                    <tr>
                                        <th scope="row">{i18n.get('apartment.application.reason')}</th>
                                        <td>{this.data.application?.reason}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div>
                            <h3>{i18n.get('apartment.application.steps.4')}</h3>
                            <table>
                                <tbody>
                                    {DOCUMENT_FIELDS[this.apartmentState?.apartmentType]?.map(field => (
                                        <Fragment>
                                            <tr>
                                                <th scope="row">{i18n.get(`apartment.application.documents.${field.type}`)}</th>
                                                <td>
                                                    <ul>
                                                        {this.documents?.[field.type]?.map(file => (
                                                            <li>
                                                                <a href={URL.createObjectURL(file)} target="_blank">{file.name}</a>
                                                                {' '}
                                                                ({formatFileSize(file.size)})
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            </tr>
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {this.data.flatmates?.length ? (
                            <div>
                                <h3>{i18n.get('apartment.application.steps.3')}</h3>
                                <table>
                                    <tbody>
                                        {this.data.flatmates.map((flatmate, index) => (
                                            <Fragment>
                                                <tr>
                                                    <th scope="row">{i18n.get('apartment.application.flatmate', null, [(index + 1).toString()])}</th>
                                                    <td>
                                                        {flatmate.lastName} {flatmate.firstName}
                                                        <br />
                                                        {flatmate.birthdate
                                                            ? i18n.get('apartment.application.person.birthdate.format', null, flatmate.birthdate.split('-'))
                                                            : null
                                                        }
                                                        <br />
                                                        {flatmate.relation
                                                            ? i18n.get(`apartment.application.flatmate.relation.${flatmate.relation}`)
                                                            : null
                                                        }
                                                    </td>
                                                </tr>
                                            </Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div class="apartment-application-checkboxes">
                    <h3>{i18n.get('apartment.application.confirmation.complete')}</h3>

                    <label class="apartment-application-label apartment-application-required">
                        <input type="checkbox" required={true} />
                        {i18n.get('apartment.application.confirmation.checkboxResidence')}
                    </label>

                    <label class="apartment-application-label apartment-application-required">
                        <input type="checkbox" required={true} />
                        {i18n.get('apartment.application.confirmation.checkboxComplete')}
                    </label>

                    <label class="apartment-application-label apartment-application-required">
                        <input type="checkbox" required={true} />
                        {i18n.get('apartment.application.confirmation.checkboxData')}
                    </label>
                </div>

                {!this.valid ? (
                    <p class="apartment-application-error">
                        {i18n.get('apartment.application.confirmation.invalid')}
                    </p>
                ) : null}
                {this.documentsError ? (
                    <p class="apartment-application-error">
                        {i18n.get('apartment.application.confirmation.documentsError')}
                    </p>
                ) : null}
                {this.sendError ? (
                    <p class="apartment-application-error">
                        {i18n.get('apartment.application.confirmation.sendError')}
                    </p>
                ) : null}

                <div class="apartment-application-buttons">
                    {this.renderPreviousButton()}
                    {this.loading ? (
                        <div class="apartment-application-loading"></div>
                    ) : null}
                    {this.renderSubmitButton('apartment.application.confirmation.send', this.loading)}
                </div>
            </Fragment>
        );
    }

    private renderDone(): JSX.Element {
        return (
            <Fragment>
                <div innerHTML={i18n.get('apartment.application.done')} class="apartment-application-content"></div>

                <div class="apartment-application-buttons">
                    <button class="apartment-application-button apartment-application-button-primary" type="button" onClick={this.onCloseClick}>
                        {i18n.get('apartment.application.dialog.close')}
                    </button>
                </div>
            </Fragment>
        );
    }

    private renderStep(): JSX.Element {
        switch (this.step) {
            case ApartmentApplicationStep.EMPLOYEE:
                return this.renderEmployeeData();
            case ApartmentApplicationStep.CUSTOMER:
                return this.renderCustomerData();
            case ApartmentApplicationStep.APPLICATION:
                return this.renderApplicationData();
            case ApartmentApplicationStep.FLATMATES:
                return this.renderFlatmates();
            case ApartmentApplicationStep.DOCUMENTS:
                return this.renderDocuments();
            case ApartmentApplicationStep.CONFIRMATION:
                return this.renderConfirmation();
            case ApartmentApplicationStep.DONE:
                return this.renderDone();
        }
    }

    private renderProgressItem(step: ApartmentApplicationStep): JSX.Element {
        return (
            <li class={{
                'apartment-application-progress-active': this.step === step,
                'apartment-application-progress-done': this.step > step,
            }}>
                {step < ApartmentApplicationStep.DONE ? (
                    <span class="apartment-application-progress-line"></span>
                ) : null}
                <button class="apartment-application-progress-button"
                        type="button"
                        onClick={this.jumpToStep}
                        data-step={step}
                        disabled={this.step >= ApartmentApplicationStep.DONE || step > this.step}
                >
                    <span class="apartment-application-progress-point">{step + 1}</span>
                    <span class="apartment-application-progress-text">{i18n.get(`apartment.application.steps.${step}`)}</span>
                </button>
            </li>
        );
    }

    private renderProgress(): JSX.Element {
        return (
            <ul class="apartment-application-progress">
                {this.renderProgressItem(ApartmentApplicationStep.EMPLOYEE)}
                {this.renderProgressItem(ApartmentApplicationStep.CUSTOMER)}
                {this.renderProgressItem(ApartmentApplicationStep.APPLICATION)}
                {this.renderProgressItem(ApartmentApplicationStep.FLATMATES)}
                {this.renderProgressItem(ApartmentApplicationStep.DOCUMENTS)}
                {this.renderProgressItem(ApartmentApplicationStep.CONFIRMATION)}
                {this.renderProgressItem(ApartmentApplicationStep.DONE)}
            </ul>
        );
    }

    private renderContent(): JSX.Element {
        if (this.apartmentStateLoading) {
            return (
                <div class="apartment-application-loading"></div>
            );
        } else if (this.backendDisabled) {
            return (
                <div innerHTML={i18n.get('apartment.application.state.backendDisabled')} class="apartment-application-error">
                </div>
            );
        } else if (this.apartmentStateError) {
            return (
                <div class="apartment-application-error">
                    {i18n.get('apartment.application.state.error')}
                </div>
            );
        } else if (!this.apartmentState?.online) {
            return (
                <div class="apartment-application-error">
                    {i18n.get('apartment.application.state.offline')}
                </div>
            );
        } else {
            return (
                <form class={{'apartment-application-form': true, 'form-invalid': !this.valid}} onSubmit={this.onFormSubmit}>
                    {this.renderProgress()}
                    {this.renderStep()}
                </form>
            );
        }
    }

    render(): JSX.Element {
        return (
            <div class="apartment-application">
                {this.renderContent()}
            </div>
        );
    }
}
